/**
 * The feature flag registry: every flag this codebase branches on, defined once.
 *
 * Definitions live in code because a flag only means something once code reads it; who holds a
 * flag is data and lives in the `featureFlagGrants` Convex table. Both `packages/convex` and
 * `apps/web-app` import these keys, so a misspelt key is a type error on either side.
 *
 * The registry is legitimately empty between the last flag's retirement and the next one's
 * introduction. Everything here — and every backend path that reads it — must keep working in
 * that state, which is why grant arguments carry a plain string validated through
 * {@link isFeatureFlagKey} rather than a generated literal union, and why every helper body is
 * written against the string-keyed {@link FeatureFlagRegistryView} while the exported signatures
 * stay keyed by {@link FeatureFlagKey}. With no keys left, `FeatureFlagKey` is `never` and every
 * map here is `{}`; only the product call sites that still name a retired key fail to compile,
 * which is exactly the reminder the implementer needs.
 */
export const FEATURE_FLAG_KEYS = [
    "version_snapshots",
    "chat_copy_markdown",
    "quality_guard_2",
];
export const FEATURE_FLAGS = {
    version_snapshots: {
        description: "Project-wide Version snapshots page and its Convex functions",
        kind: "rollout",
        defaultEnabled: false,
    },
    chat_copy_markdown: {
        description: "Copy as Markdown action on Claudia chat messages",
        kind: "rollout",
        defaultEnabled: false,
    },
    quality_guard_2: {
        description: "Quality Guard 2 as the engine of record for scans, findings and the entity panel",
        kind: "rollout",
        defaultEnabled: false,
    },
};
/** Where a flag's enablement came from. Sources are additive: any one of them enables. */
export const FEATURE_FLAG_SOURCE_KINDS = ["global", "org", "user", "default"];
/** The scopes a grant row can target. `global` carries no id; the other two carry exactly one. */
export const FEATURE_FLAG_GRANT_SCOPES = ["global", "org", "user"];
/**
 * Build a registry from a key tuple and its definitions.
 *
 * The definitions object is read on every call rather than copied, so a deployment that swaps a
 * definition swaps what every helper answers. `keys` may be empty: the bodies iterate a
 * `readonly string[]` and write into string-keyed records, and the typed façade is a cast over
 * the same objects.
 */
export function createFeatureFlagRegistry(keys, definitions) {
    const keyList = keys;
    const byKey = definitions;
    const keySet = new Set(keyList);
    const view = {
        keys: keyList,
        isKey: (key) => keySet.has(key),
        definition: (key) => (keySet.has(key) ? byKey[key] : undefined),
        list: () => keyList.flatMap((key) => {
            const definition = byKey[key];
            return definition ? [{ key, ...definition }] : [];
        }),
        emptyMap: () => {
            const map = {};
            for (const key of keyList)
                map[key] = false;
            return map;
        },
        emptySourceMap: () => {
            const map = {};
            for (const key of keyList)
                map[key] = [];
            return map;
        },
        mapFromSources: (sources) => {
            const map = {};
            for (const key of keyList)
                map[key] = (sources[key]?.length ?? 0) > 0;
            return map;
        },
        isCleanupEligible: (key) => {
            const definition = keySet.has(key) ? byKey[key] : undefined;
            // A key the registry no longer holds grants nothing, so sweeping its rows is hygiene.
            if (!definition)
                return true;
            return definition.kind === "rollout" && definition.defaultEnabled;
        },
    };
    return {
        keys: keyList,
        isKey: (key) => view.isKey(key),
        definition: (key) => {
            const definition = view.definition(key);
            if (!definition) {
                throw new Error(`Feature flag '${String(key)}' is in the key list with no definition.`);
            }
            return definition;
        },
        list: () => view.list(),
        emptyMap: () => view.emptyMap(),
        emptySourceMap: () => view.emptySourceMap(),
        mapFromSources: (sources) => view.mapFromSources(sources),
        isCleanupEligible: (key) => view.isCleanupEligible(key),
        view,
    };
}
const REGISTRY = createFeatureFlagRegistry(FEATURE_FLAG_KEYS, FEATURE_FLAGS);
/** This deployment's registry addressed by plain string, for code holding keys as data. */
export const FEATURE_FLAG_REGISTRY_VIEW = REGISTRY.view;
export function isFeatureFlagKey(key) {
    return REGISTRY.isKey(key);
}
/** The definition of a key the type system already proved is in the registry. */
export function getFeatureFlagDefinition(key) {
    return REGISTRY.definition(key);
}
export function listFeatureFlagDefinitions() {
    return REGISTRY.list();
}
/** Every registry key set to `false` — the map a pending or unauthenticated caller reads. */
export function emptyFeatureFlagMap() {
    return REGISTRY.emptyMap();
}
/** Every registry key mapped to an empty source list, ready to be added to. */
export function emptyFeatureFlagSourceMap() {
    return REGISTRY.emptySourceMap();
}
/** The one rule turning sources into booleans: a flag is on when any source enables it. */
export function featureFlagMapFromSources(sources) {
    return REGISTRY.mapFromSources(sources);
}
/**
 * Whether cleanup may bulk-revoke every grant of `flagKey`.
 *
 * Eligible when the key has left the registry, or when it is a rollout flag that is already on
 * for everyone. An entitlement flag, or a rollout flag still default-off, is never eligible:
 * revoking those would disable a live or paid capability.
 */
export function isFeatureFlagCleanupEligible(flagKey) {
    return REGISTRY.isCleanupEligible(flagKey);
}
